/**
 * Where you are, as a trail back to the top of the console.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A BREADCRUMB WAS TRIED HERE BEFORE AND REMOVED. THIS IS A DIFFERENT ONE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `App.tsx` records the first attempt and why `HeaderScope` replaced it: that breadcrumb was
 * trying to say which ORGANISATION, SITE and PLANT the page was showing, and a trail is the
 * wrong shape for that — those three are a scope the reader SETS, not a path they walked, and
 * a reader who changes one is not navigating anywhere.
 *
 * This one answers the other question. It says which SCREEN you are on and offers the way back
 * to the top, which is a path and which nothing in the header says today. The two coexist
 * because they are different facts: the trail sits left of the divider with the wordmark, the
 * scope picker stays in the right-hand cluster. If they are ever merged, the merged thing will
 * be the first one again.
 *
 * ── IT IS NOT A LINK, BECAUSE THERE IS NO ROUTER ───────────────────────────────
 * Navigation here is a `tab` string in `App`'s state (AGENTS.md: "There is no router"), so the
 * crumb takes `onTab` and calls it — the `leadsTo` pattern the rest of the console uses. It is
 * a `<button>`, not an `<a href>`, because an anchor without an href is not operable by
 * keyboard and one WITH a fabricated href would offer a middle-click that opens nothing.
 *
 * ── AND IT COLLAPSES RATHER THAN WRAPS ─────────────────────────────────────────
 * The header is one 60px row and the trail shares it with a wordmark and six controls, so
 * below the narrow breakpoint the leading label drops to its icon and the current screen's
 * name is what survives. Losing "Home" costs the reader a hover; losing the current screen's
 * name would cost them the only thing the trail is for.
 */
import type { ReactNode } from 'react';
import { Icon } from './Icon';
import { NAV_LABEL } from '@/app/nav';

export interface BreadcrumbProps {
  /** The active tab id, as `App` holds it. */
  tab: string;
  /** Where the trail's root goes — the product's own landing screen. */
  homeTab: string;
  onTab: (tab: string) => void;
}

export function Breadcrumb({ tab, homeTab, onTab }: BreadcrumbProps): ReactNode {
  const atHome = tab === homeTab;
  /*
   * An unknown id still renders a trail rather than an empty one. `NAV_LABEL` covers every
   * rail entry plus the two routable non-rail screens, but `tab` is a string from the URL and
   * a bookmark can carry anything — and a header that silently loses its trail is harder to
   * diagnose than one that says the id out loud.
   */
  const here = NAV_LABEL[tab] ?? tab;

  return (
    <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-[8px]">
      <button
        type="button"
        onClick={() => onTab(homeTab)}
        disabled={atHome}
        className="flex items-center gap-[7px] text-[var(--fs-sm)] font-medium whitespace-nowrap"
        style={{ color: atHome ? 'var(--fg)' : 'var(--muted-fg)', cursor: atHome ? 'default' : 'pointer' }}
        {...(atHome ? { 'aria-current': 'page' as const } : { title: `Back to ${NAV_LABEL[homeTab] ?? 'the start'}` })}
      >
        <Icon name={homeTab === 'home' ? 'ic-home' : 'ic-gauge'} size={16} />
        <span className="hidden sm:inline">{NAV_LABEL[homeTab] ?? 'Home'}</span>
      </button>
      {atHome ? null : (
        <>
          <span aria-hidden style={{ color: 'var(--muted-fg)', opacity: 0.6 }}>
            /
          </span>
          <span
            aria-current="page"
            className="truncate text-[var(--fs-sm)] font-medium"
            style={{ color: 'var(--fg)' }}
          >
            {here}
          </span>
        </>
      )}
    </nav>
  );
}
